function toNumber(value, fallback) {
  const parsed = Number(value)
  if (Number.isFinite(parsed)) {
    return parsed
  }
  return fallback
}

function mountFactor(installationType, roofType) {
  if (installationType === 'ground') {
    return 1
  }
  if (roofType === 'sandwich') {
    return 0.98
  }
  if (roofType === 'tile') {
    return 0.95
  }
  if (roofType === 'flat') {
    return 0.97
  }
  if (roofType === 'bitumen') {
    return 0.94
  }
  return 0.95
}

function monthlyDistribution(baseProduction) {
  const factors = [0.62, 0.7, 0.86, 1, 1.1, 1.18, 1.22, 1.15, 1, 0.86, 0.72, 0.59]
  return factors.map((factor) => baseProduction * factor)
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max)
}

function initializeCalculator(section) {
  const monthlyBillInput = section.querySelector('[name="monthly_bill"]')
  const electricityPriceInput = section.querySelector('[name="electricity_price"]')
  const knowKwhInput = section.querySelector('[name="know_kwh"]')
  const monthlyKwhInput = section.querySelector('[name="monthly_kwh"]')
  const billField = section.querySelector('[data-bill-field]')
  const kwhField = section.querySelector('[data-kwh-field]')
  const sunHoursInput = section.querySelector('[name="sun_hours"]')
  const sunHoursOutput = section.querySelector('#solar-sun-hours-output-' + section.dataset.sectionId)
  const roofTypeField = section.querySelector('[data-roof-type-field]')
  const batteryInput = section.querySelector('[name="battery_enabled"]')
  const batteryVoltageField = section.querySelector('[data-battery-voltage-field]')
  const efficiencyInput = section.querySelector('[name="system_efficiency"]')
  const efficiencyOutput = section.querySelector('#solar-efficiency-output-' + section.dataset.sectionId)
  const warningOutput = section.querySelector('[data-phase-warning]')

  const resultSystemSize = section.querySelector('[data-result-system-size]')
  const resultCoverage = section.querySelector('[data-result-coverage]')
  const resultPanelCount = section.querySelector('[data-result-panel-count]')
  const resultMonthlyProduction = section.querySelector('[data-result-monthly-production]')
  const resultAnnualProduction = section.querySelector('[data-result-annual-production]')
  const resultRoofArea = section.querySelector('[data-result-roof-area]')
  const resultMonthlySavings = section.querySelector('[data-result-monthly-savings]')
  const resultYearlySavings = section.querySelector('[data-result-yearly-savings]')
  const chartCanvas = section.querySelector('[data-chart-canvas]')
  let chartInstance = null

  function monthLabels() {
    const fallbackLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    return fallbackLabels.map((fallbackLabel, index) => {
      const attributeValue = section.getAttribute('data-i18n-month-' + String(index + 1))
      return attributeValue && attributeValue.trim() ? attributeValue : fallbackLabel
    })
  }

  function readOrientationFactor() {
    const selected = section.querySelector('[name="orientation_factor"]:checked')
    return toNumber(selected ? selected.value : 1, 1)
  }

  function readRadioValue(name, fallback) {
    const selected = section.querySelector('[name="' + name + '"]:checked')
    if (!selected) {
      return fallback
    }
    return selected.value
  }

  function isHybrid(systemType) {
    if (!systemType) {
      return false
    }
    const normalized = systemType.toLowerCase()
    return normalized.includes('hybrid') || normalized.includes('хибрид')
  }

  function isSinglePhase(systemType) {
    if (!systemType) {
      return false
    }
    const normalized = systemType.toLowerCase()
    return normalized.includes('single_phase') || normalized.includes('монофаз')
  }

  function updateToggleButtons() {
    const options = section.querySelectorAll('[data-radio-option], [data-orientation-option]')
    options.forEach((option) => {
      const radio = option.querySelector('input[type="radio"]')
      if (radio && radio.checked) {
        option.classList.add('button')
        option.classList.remove('button-secondary')
      } else {
        option.classList.remove('button')
        option.classList.add('button-secondary')
      }
    })
  }

  function updateBatteryAvailability(systemType) {
    const hybrid = isHybrid(systemType)
    if (!hybrid) {
      batteryInput.checked = false
    }
    batteryInput.disabled = !hybrid
  }

  function updateVisibility() {
    const knowKwh = knowKwhInput.checked
    const roofSelected = readRadioValue('installation_type', 'ground') === 'roof'
    kwhField.hidden = !knowKwh
    billField.hidden = knowKwh
    monthlyKwhInput.disabled = !knowKwh
    monthlyBillInput.disabled = knowKwh
    roofTypeField.hidden = !roofSelected
    if (batteryVoltageField) {
      batteryVoltageField.hidden = !batteryInput.checked
    }
  }

  function currentMonthlyKwh() {
    return Math.max(toNumber(monthlyKwhInput.value, 0), 0)
  }

  function currentElectricityPrice() {
    return Math.max(toNumber(electricityPriceInput.value, 0.15), 0)
  }

  function syncKwhFromBill() {
    const electricityPrice = currentElectricityPrice()
    const monthlyBill = Math.max(toNumber(monthlyBillInput.value, 0), 0)
    const monthlyKwh = electricityPrice > 0 ? monthlyBill / electricityPrice : 0
    monthlyKwhInput.value = monthlyKwh.toFixed(0)
  }

  function syncBillFromKwh() {
    const electricityPrice = currentElectricityPrice()
    const monthlyKwh = currentMonthlyKwh()
    monthlyBillInput.value = String(Math.round(monthlyKwh * electricityPrice))
  }

  function renderChart(monthlyData) {
    if (!chartCanvas || !window.Chart) {
      return
    }

    const labels = monthLabels()
    const axisTitle = section.dataset.i18nChartAxisEnergy || 'kWh'

    if (!chartInstance) {
      chartInstance = new window.Chart(chartCanvas, {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              data: monthlyData,
              borderRadius: 4,
              borderSkipped: false,
              backgroundColor: '#339b00',
              hoverBackgroundColor: '#339b00',
              categoryPercentage: 0.78,
              barPercentage: 0.9,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              callbacks: {
                label(context) {
                  return context.parsed.y.toFixed(0) + ' kWh'
                },
              },
            },
          },
          scales: {
            x: {
              grid: {
                display: false,
                drawBorder: false,
              },
              ticks: {
                color: 'rgba(26, 40, 67, 0.78)',
                maxRotation: 0,
                autoSkip: false,
                font: {
                  size: 11,
                },
              },
            },
            y: {
              beginAtZero: true,
              ticks: {
                precision: 0,
                color: 'rgba(26, 40, 67, 0.68)',
                font: {
                  size: 11,
                },
              },
              title: {
                display: true,
                text: axisTitle,
                color: 'rgba(26, 40, 67, 0.72)',
                font: {
                  size: 12,
                  weight: '600',
                },
              },
              grid: {
                color: 'rgba(26, 40, 67, 0.1)',
                drawBorder: false,
              },
            },
          },
        },
      })
      return
    }

    chartInstance.data.labels = labels
    chartInstance.data.datasets[0].data = monthlyData
    chartInstance.options.scales.y.title.text = axisTitle
    chartInstance.update('none')
  }

  function recalculate() {
    const electricityPrice = currentElectricityPrice()
    if (knowKwhInput.checked) {
      syncBillFromKwh()
    } else {
      syncKwhFromBill()
    }
    const monthlyKwh = currentMonthlyKwh()
    const sunHours = clamp(toNumber(sunHoursInput.value, 5.5), 2, 8)
    const orientationFactor = readOrientationFactor()
    const installationType = readRadioValue('installation_type', 'ground')
    const roofType = readRadioValue('roof_type', 'sandwich')
    const systemType = readRadioValue('system_type', 'single_phase')
    const panelPower = Math.max(toNumber(readRadioValue('panel_power', '500'), 500), 100)
    const mount = mountFactor(installationType, roofType)
    const rawEfficiency = toNumber(efficiencyInput.value, 78)
    const efficiencyPercent = clamp(
      Math.round(rawEfficiency > 0 && rawEfficiency <= 1 ? rawEfficiency * 100 : rawEfficiency),
      60,
      95
    )
    const systemEfficiency = efficiencyPercent / 100

    updateBatteryAvailability(systemType)
    updateVisibility()
    updateToggleButtons()
    const selfConsumption = isHybrid(systemType) ? 0.9 : 0.75

    const dailyProductionPerKw = sunHours * orientationFactor * mount * systemEfficiency
    const monthlyProductionPerKw = dailyProductionPerKw * 30
    const systemSizeKw = monthlyProductionPerKw > 0 ? monthlyKwh / monthlyProductionPerKw : 0
    const producedEnergy = systemSizeKw * monthlyProductionPerKw
    const usableEnergy = producedEnergy * selfConsumption
    const coverage = monthlyKwh > 0 ? (usableEnergy / monthlyKwh) * 100 : 0
    const panelCount = Math.ceil((systemSizeKw * 1000) / panelPower)
    const roofArea = panelCount * 1.63
    const monthlySavings = usableEnergy * electricityPrice
    const annualProduction = producedEnergy * 12
    const yearlySavings = monthlySavings * 12
    const monthlySeries = monthlyDistribution(producedEnergy)

    resultSystemSize.textContent = systemSizeKw.toFixed(2)
    resultCoverage.textContent = Math.min(coverage, 999).toFixed(1)
    resultPanelCount.textContent = Number.isFinite(panelCount) ? panelCount : 0
    resultMonthlyProduction.textContent = producedEnergy.toFixed(0)
    resultAnnualProduction.textContent = annualProduction.toFixed(0)
    resultRoofArea.textContent = roofArea.toFixed(1)
    resultMonthlySavings.textContent = monthlySavings.toFixed(2)
    resultYearlySavings.textContent = yearlySavings.toFixed(2)

    const publishedSize = Number.isFinite(systemSizeKw) && systemSizeKw > 0 ? systemSizeKw.toFixed(3) : '0'
    if (section.dataset.systemSizeKw !== publishedSize) {
      section.dataset.systemSizeKw = publishedSize
      section.dispatchEvent(
        new CustomEvent('solar-calculator:system-size-change', {
          detail: { systemSizeKw: Number(publishedSize) },
        })
      )
    }
    efficiencyInput.value = String(efficiencyPercent)
    efficiencyOutput.value = efficiencyPercent + '%'
    efficiencyOutput.textContent = efficiencyPercent + '%'
    sunHoursInput.value = sunHours.toFixed(1)
    if (sunHoursOutput) {
      sunHoursOutput.value = sunHours.toFixed(1)
      sunHoursOutput.textContent = sunHours.toFixed(1)
    }

    if (isSinglePhase(systemType) && !isHybrid(systemType) && systemSizeKw > 6) {
      warningOutput.hidden = false
      warningOutput.textContent = section.dataset.i18nWarningSinglePhase
    } else {
      warningOutput.hidden = true
      warningOutput.textContent = ''
    }

    renderChart(monthlySeries)

    monthlyKwhInput.value = monthlyKwh.toFixed(0)
  }

  monthlyBillInput.addEventListener('input', () => {
    if (!knowKwhInput.checked) {
      syncKwhFromBill()
    }
    recalculate()
  })

  electricityPriceInput.addEventListener('input', () => {
    if (knowKwhInput.checked) {
      syncBillFromKwh()
    } else {
      syncKwhFromBill()
    }
    recalculate()
  })

  monthlyKwhInput.addEventListener('input', () => {
    syncBillFromKwh()
    recalculate()
  })

  sunHoursInput.addEventListener('input', recalculate)
  efficiencyInput.addEventListener('input', recalculate)

  section.addEventListener('change', recalculate)
  recalculate()
}

function initializeSuggestions(section) {
  const suggestionsRoot = section.querySelector('[data-solar-suggestions]')
  const resultsContainer = section.querySelector('[data-solar-suggestions-results]')
  const resultsPanel = section.querySelector('[data-solar-suggestions-panel]')
  const configElement = section.querySelector('[data-solar-suggestion-config]')

  if (!suggestionsRoot || !resultsContainer || !resultsPanel || !configElement) {
    return
  }

  let config
  try {
    config = JSON.parse(configElement.textContent || '{}')
  } catch (error) {
    return
  }

  const suggestions = Array.isArray(config.suggestions) ? config.suggestions : []
  if (suggestions.length === 0) {
    return
  }

  const suggestionSectionId = suggestionsRoot.dataset.suggestionSectionId || 'solar-calculator-suggestion'
  const collectionUrl = suggestionsRoot.dataset.suggestionCollectionUrl || '/collections/all'
  const emptyMessage = section.dataset.i18nSuggestionsEmpty || ''
  const errorMessage = section.dataset.i18nSuggestionsError || ''
  const loadingMessage = section.dataset.i18nSuggestionsLoading || ''

  function isEntryActive(entry) {
    if (!entry || !entry.requires || !entry.requires.inputName) {
      return true
    }
    const gatingInput = section.querySelector('input[name="' + entry.requires.inputName + '"]')
    if (!gatingInput) {
      return true
    }
    return gatingInput.checked === entry.requires.checked
  }

  function readSystemSizeKw() {
    const parsed = Number(section.dataset.systemSizeKw)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
  }

  function findKwOptionForSize(options, target) {
    if (!Array.isArray(options) || options.length === 0 || target <= 0) {
      return null
    }
    const sorted = options
      .map((option) => ({ option, value: Number(option.label) }))
      .filter((entry) => Number.isFinite(entry.value))
      .sort((a, b) => a.value - b.value)
    if (sorted.length === 0) {
      return null
    }
    for (let i = 0; i < sorted.length; i += 1) {
      if (sorted[i].value >= target) {
        return sorted[i].option
      }
    }
    return sorted[sorted.length - 1].option
  }

  function composeUrl(baseParams, extras, sortBy) {
    const params = new URLSearchParams()
    Object.keys(baseParams).forEach((key) => {
      params.set(key, baseParams[key])
    })
    if (extras) {
      Object.keys(extras).forEach((key) => {
        params.set(key, extras[key])
      })
    }
    params.set('sort_by', sortBy || 'price-ascending')
    params.set('section_id', suggestionSectionId)
    return collectionUrl + '?' + params.toString()
  }

  function buildUrlsForEntry(entry) {
    if (!entry || !Array.isArray(entry.values) || entry.values.length === 0) {
      return []
    }
    if (!isEntryActive(entry)) {
      return []
    }
    const selected = section.querySelector('input[name="' + entry.inputName + '"]:checked')
    if (!selected) {
      return []
    }
    const match = entry.values.find((candidate) => candidate.value === selected.value)
    if (!match || !match.paramName || match.paramValue == null) {
      return []
    }

    const baseParams = { [match.paramName]: match.paramValue }

    if (entry.type === 'inverter' && entry.powerKw) {
      const urls = []
      const bracket = findKwOptionForSize(entry.powerKw.values, readSystemSizeKw())
      if (bracket && entry.powerKw.paramName && bracket.paramValue != null) {
        urls.push(
          composeUrl(baseParams, { [entry.powerKw.paramName]: bracket.paramValue }, 'most-relevant')
        )
      }
      urls.push(composeUrl(baseParams, null, 'most-relevant'))
      return urls
    }

    return [composeUrl(baseParams, null, 'price-ascending')]
  }

  function fragmentHasProduct(fragment) {
    return typeof fragment === 'string' && fragment.indexOf('data-solar-suggestion-card') !== -1
  }

  function extractSectionContent(html) {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    const wrapper = doc.querySelector('[id^="shopify-section-"]')
    if (wrapper) {
      return wrapper.innerHTML.trim()
    }
    return html.trim()
  }

  async function fetchSuggestion(url, signal) {
    const response = await fetch(url, {
      headers: { Accept: 'text/html' },
      credentials: 'same-origin',
      signal,
    })
    if (!response.ok) {
      throw new Error('Suggestion request failed: ' + response.status)
    }
    const html = await response.text()
    return extractSectionContent(html)
  }

  function renderMessage(message, modifier) {
    const className = 'solar-suggestions__message' + (modifier ? ' solar-suggestions__message--' + modifier : '')
    return '<p class="' + className + '">' + message + '</p>'
  }

  let activeController = null
  let debounceTimer = null
  const AUTO_DEBOUNCE_MS = 250

  async function loadSuggestions() {
    if (activeController) {
      activeController.abort()
    }
    const controller = new AbortController()
    activeController = controller

    resultsPanel.hidden = false
    resultsContainer.setAttribute('aria-busy', 'true')
    resultsContainer.dataset.updating = 'true'

    const isFirstLoad = resultsContainer.childElementCount === 0
    if (isFirstLoad) {
      resultsContainer.innerHTML = renderMessage(loadingMessage, 'loading')
    }

    const urlGroups = suggestions.map(buildUrlsForEntry).filter((urls) => urls.length > 0)

    console.log('[solar-calculator] suggestion URL groups:', {
      systemSizeKw: readSystemSizeKw(),
      groups: urlGroups,
    })

    if (urlGroups.length === 0) {
      resultsContainer.innerHTML = renderMessage(emptyMessage, 'empty')
      finalizeLoad(controller)
      return
    }

    async function resolveGroup(urls) {
      let lastFragment = ''
      for (let i = 0; i < urls.length; i += 1) {
        try {
          const fragment = await fetchSuggestion(urls[i], controller.signal)
          if (fragmentHasProduct(fragment)) {
            return fragment
          }
          if (fragment) {
            lastFragment = fragment
          }
        } catch (error) {
          if (error && error.name === 'AbortError') {
            throw error
          }
        }
      }
      return lastFragment
    }

    try {
      const htmlFragments = await Promise.all(urlGroups.map((urls) => resolveGroup(urls)))

      if (controller.signal.aborted) {
        return
      }

      const filtered = htmlFragments.filter((fragment) => fragment && fragment.length > 0)
      if (filtered.length === 0) {
        resultsContainer.innerHTML = renderMessage(emptyMessage, 'empty')
      } else {
        resultsContainer.innerHTML = filtered.join('')
      }
    } catch (error) {
      if (error && error.name === 'AbortError') {
        return
      }
      if (isFirstLoad) {
        resultsContainer.innerHTML = renderMessage(errorMessage, 'error')
      }
    } finally {
      finalizeLoad(controller)
    }
  }

  function finalizeLoad(controller) {
    if (activeController !== controller) {
      return
    }
    activeController = null
    resultsContainer.removeAttribute('aria-busy')
    delete resultsContainer.dataset.updating
  }

  function scheduleAutoLoad() {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }
    debounceTimer = setTimeout(() => {
      debounceTimer = null
      loadSuggestions()
    }, AUTO_DEBOUNCE_MS)
  }

  const gatingInputNames = new Set()
  let hasInverterEntry = false
  suggestions.forEach((entry) => {
    if (!entry || !Array.isArray(entry.values) || entry.values.length === 0) {
      return
    }
    if (entry.type === 'inverter') {
      hasInverterEntry = true
    }
    section.querySelectorAll('input[name="' + entry.inputName + '"]').forEach((input) => {
      input.addEventListener('change', scheduleAutoLoad)
    })
    if (entry.requires && entry.requires.inputName) {
      gatingInputNames.add(entry.requires.inputName)
    }
  })
  gatingInputNames.forEach((name) => {
    section.querySelectorAll('input[name="' + name + '"]').forEach((input) => {
      input.addEventListener('change', scheduleAutoLoad)
    })
  })

  if (hasInverterEntry) {
    section.addEventListener('solar-calculator:system-size-change', scheduleAutoLoad)
  }
}

document.querySelectorAll('.solar-calculator[data-section-id]').forEach((section) => {
  initializeCalculator(section)
  initializeSuggestions(section)
})
